/**
 * Recherche unique de l'application (décision du 17/10/2026) : plein écran
 * sur téléphone, fenêtre centrée sur ordinateur (Ctrl+K ou ⌘+K). Elle trouve
 * les clients, sites, machines et articles (§16.2), les devis, factures et
 * interventions par leur référence (B11), chacun selon les droits du compte
 * vérifiés par le serveur, et les rubriques que le compte peut ouvrir ; un QR
 * code se scanne depuis la même fenêtre.
 * Clavier : flèches entre les résultats, Entrée ouvre, Échap referme.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ScanLine, Search, X, type LucideIcon } from 'lucide-react';
import { api } from '../../core/api.js';
import { t } from '../../core/i18n.js';
import { ICONS } from '../../design/icons.js';
import { useBreakpoint } from '../../design/tokens.js';
import { Overlay } from '../overlay.js';
import { useDebouncedValue } from '../ui/ListToolbar.js';
import type { Area } from '../../app/areas.js';

interface SearchResult {
  id: string;
  label: string;
  kind: string;
  customer_id?: string;
}

const KIND_ROUTE: Record<string, (result: SearchResult) => string> = {
  customer: (result) => `/clients/${result.id}`,
  equipment: (result) => `/equipements/${result.id}`,
  // La fiche client ouvre l'onglet qui montre le site (ses machines, sinon l'aperçu).
  site: (result) => (result.customer_id ? `/clients/${result.customer_id}#site-${result.id}` : '/clients'),
  article: (result) => `/stock/articles/${result.id}`,
  quote: (result) => `/devis/${result.id}`,
  invoice: (result) => `/factures/${result.id}`,
  intervention: (result) => `/interventions/${result.id}`,
};
const KIND_ICON: Record<string, LucideIcon> = {
  customer: ICONS.clients,
  equipment: ICONS.machine,
  site: ICONS.site,
  article: ICONS.stock,
  quote: ICONS.devis,
  invoice: ICONS.ventes,
  intervention: ICONS.intervention,
};

interface Option {
  key: string;
  label: string;
  kind: string;
  icon: LucideIcon | undefined;
  to: string;
}

/** Comparaison sans accents ni casse : « facture » trouve « Factures ». */
function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

/** Ouvre la recherche au clavier, de n'importe quel écran de la coque. */
export function useSearchShortcut(open: () => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== 'k' || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      event.preventDefault();
      open();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);
}

/** Bouton de la barre du haut : une loupe au téléphone, un faux champ sur ordinateur. */
export function SearchButton({ onOpen }: { onOpen: () => void }): ReactNode {
  return (
    <button
      type="button"
      className="topbar-search"
      aria-haspopup="dialog"
      aria-keyshortcuts={IS_MAC ? 'Meta+K' : 'Control+K'}
      onClick={(event) => {
        event.currentTarget.focus();
        onOpen();
      }}
    >
      <Search size={20} aria-hidden="true" />
      {/* Nom accessible tiré du texte visible (WCAG 2.5.3) : « Rechercher », puis
          l'indication affichée sur ordinateur (masquée au téléphone). */}
      <span className="sr-only">{t('commun.rechercher')} : </span>
      <span className="topbar-search__placeholder">{t('commun.rechercherPlaceholder')}</span>
      <kbd className="topbar-search__keys" aria-hidden="true">{IS_MAC ? '⌘ K' : 'Ctrl K'}</kbd>
    </button>
  );
}

export function SearchDialog(props: {
  areas: readonly Area[];
  onClose: () => void;
  onScan: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const term = useDebouncedValue(query.trim(), 250);
  const wide = useBreakpoint('md');

  useEffect(() => {
    input.current?.focus();
  }, []);

  const records = useQuery({
    queryKey: ['search', term],
    queryFn: () => api<SearchResult[]>(`/search?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2,
    staleTime: 30_000,
    retry: false,
  });

  const options = useMemo<Option[]>(() => {
    if (term.length < 2) return [];
    const wanted = fold(term);
    // Une rubrique se trouve par son nom ou par l'un de ses onglets (« facture » ouvre Factures).
    const sections = props.areas
      .flatMap((area) => [
        { label: area.label, to: area.to, icon: area.icon },
        ...(area.tabs ?? []).map((tab) => ({ ...tab, icon: area.icon })),
      ])
      .filter((entry) => fold(entry.label).includes(wanted))
      .filter((entry, index, all) => all.findIndex((other) => other.to === entry.to) === index)
      .slice(0, 4)
      .map((entry) => ({ key: `rubrique:${entry.to}`, label: entry.label, kind: 'Rubrique', icon: entry.icon, to: entry.to }));
    const found = (records.data ?? []).map((result) => ({
      key: `${result.kind}:${result.id}`,
      label: result.label,
      kind: t(`recherche.${result.kind}`),
      icon: KIND_ICON[result.kind],
      to: KIND_ROUTE[result.kind]?.(result) ?? '/',
    }));
    return [...sections, ...found];
  }, [term, props.areas, records.data]);

  useEffect(() => setActive(0), [term]);

  const open = (option: Option): void => {
    props.onClose();
    navigate(option.to);
  };

  const status = term.length < 2
    ? 'Au moins deux lettres : client, site, machine, article, numéro de devis, de facture ou de demande, rubrique.'
    : records.isError && options.length === 0
      ? 'Recherche impossible pour le moment. Vérifiez la connexion puis réessayez.'
      : records.isFetching && options.length === 0
        ? t('commun.chargement')
        : options.length === 0
          ? t('commun.aucunResultat')
          : undefined;

  return (
    <Overlay label={t('commun.rechercher')} onClose={props.onClose} variant={wide ? 'modale' : 'plein'}>
      <div className="shell-search">
        <div className="shell-search__bar">
          <Search className="shell-search__icon" size={20} aria-hidden="true" />
          <input
            ref={input}
            type="search"
            className="shell-search__input"
            value={query}
            placeholder={t('commun.rechercherPlaceholder')}
            aria-label={t('commun.rechercher')}
            aria-controls={listId}
            aria-activedescendant={options[active] ? `${listId}-${active}` : undefined}
            autoComplete="off"
            enterKeyHint="search"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                if (options.length === 0) return;
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setActive((current) => (current + step + options.length) % options.length);
              } else if (event.key === 'Enter') {
                const option = options[active];
                if (option) {
                  event.preventDefault();
                  open(option);
                }
              }
            }}
          />
          <button
            type="button"
            className="btn btn--ghost btn--icon"
            aria-label={t('qr.scanner')}
            title={t('qr.scanner')}
            onClick={() => {
              props.onClose();
              props.onScan();
            }}
          >
            <ScanLine size={20} aria-hidden="true" />
          </button>
          <button type="button" className="btn btn--ghost btn--icon" aria-label={t('commun.fermer')} onClick={props.onClose}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {status ? (
          <p className="shell-search__status" role="status">{status}</p>
        ) : null}
        <ul className="shell-search__results" id={listId} role="listbox" aria-label="Résultats">
          {options.map((option, index) => {
            const Icon = option.icon;
            return (
              <li
                key={option.key}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                className="shell-search__option"
                onPointerMove={() => setActive(index)}
                onClick={() => open(option)}
              >
                <span className="shell-search__option-icon" aria-hidden="true">
                  {Icon ? <Icon size={18} /> : null}
                </span>
                <span className="shell-search__option-label">{option.label}</span>
                <span className="shell-search__option-kind">{option.kind}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </Overlay>
  );
}
